import { useEffect, useRef } from "react";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { showRateLimitToast } from "@/lib/apiErrors";
import { ClerkProvider, useClerk } from "@clerk/react";
import { publishableKeyFromHost } from "@clerk/react/internal";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { basePath, clerkAppearance, clerkLocalization, stripBase } from "@/lib/clerk";

import NotFound from "@/pages/not-found";
import Home from "@/pages/home";
import Courses from "@/pages/courses";
import CourseDetail from "@/pages/course-detail";
import LessonDetail from "@/pages/lesson-detail";
import MyClasses from "@/pages/my-classes";
import About from "@/pages/about";
import ForSchools from "@/pages/for-schools";
import SignInPage from "@/pages/sign-in";
import SignUpPage from "@/pages/sign-up";
import SnippetShare from "@/pages/snippet-share";
import CheckoutSuccess from "@/pages/checkout-result";
import Classroom from "@/pages/classroom";
import TeacherDashboard from "@/pages/teacher-dashboard";
import TeacherCourseEdit from "@/pages/teacher-course-edit";
import TeacherCourseStudents from "@/pages/teacher-course-students";
import TermsPage from "@/pages/legal/terms";
import PrivacyPage from "@/pages/legal/privacy";
import CookiesPage from "@/pages/legal/cookies";

const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY in .env file");
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      showRateLimitToast(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      showRateLimitToast(error);
    },
  }),
});

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        qc.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, qc]);

  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/courses" component={Courses} />
      <Route path="/courses/:slug" component={CourseDetail} />
      <Route path="/learn/:slug" component={Classroom} />
      <Route path="/lessons/:id" component={LessonDetail} />
      <Route path="/my-classes" component={MyClasses} />
      <Route path="/teacher" component={TeacherDashboard} />
      <Route path="/teacher/courses/:slug/edit" component={TeacherCourseEdit} />
      <Route path="/teacher/courses/:slug/students" component={TeacherCourseStudents} />
      <Route path="/about" component={About} />
      <Route path="/for-schools" component={ForSchools} />
      <Route path="/sign-in/*?" component={SignInPage} />
      <Route path="/sign-up/*?" component={SignUpPage} />
      <Route path="/s/:id" component={SnippetShare} />
      <Route path="/checkout/success" component={CheckoutSuccess} />
      <Route path="/terms" component={TermsPage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/cookies" component={CookiesPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={clerkLocalization}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <TooltipProvider>
          <Router />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
